import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Where the operator last had the map (#441).
 *
 * MainLayout renders Fleet and Planner conditionally, so leaving a view
 * unmounts its MapContainer and coming back constructs a fresh Leaflet map from
 * whatever center/zoom it is given. With nothing remembered that was a
 * hardcoded London at zoom 10, so an operator working over Łódź was returned to
 * England on every tab switch.
 *
 * One viewport, shared by both maps rather than one each. #441 separated two
 * losses: a view forgetting where it was, and Fleet and Planner never agreeing
 * on a location in the first place. Per-view memory fixes only the first, and
 * the operator is looking at the same world either way — switching to the
 * planner to draw over the drone you were just watching should not require
 * finding it again.
 */
/**
 * Where a location search landed, so the map can point at it (#523).
 *
 * Searching only moved the viewport. At street zoom on a dark basemap the
 * centre of the screen is not a landmark, so an operator who searched an
 * address got a plausible-looking map and no way to tell which building it
 * meant — or whether the geocoder had understood them at all.
 */
export interface SearchPin {
  lat: number;
  lng: number;
  /** What the geocoder matched, or the coordinates as typed. */
  label: string;
}

export interface MapViewState {
  /** Last committed center, or null before the operator has moved a map. */
  center: [number, number] | null;
  /** Last committed zoom, or null before the operator has moved a map. */
  zoom: number | null;
  /** The most recent search result, or null when nothing is being pointed at. */
  searchPin: SearchPin | null;
}

const initialState: MapViewState = {
  center: null,
  zoom: null,
  searchPin: null,
};

const mapViewSlice = createSlice({
  name: 'mapView',
  initialState,
  reducers: {
    /**
     * Record where a map came to rest. Dispatched from `moveend`/`zoomend`,
     * which fire once a gesture settles rather than continuously through it.
     */
    mapViewChanged(
      state,
      action: PayloadAction<{ center: [number, number]; zoom: number }>
    ) {
      state.center = action.payload.center;
      state.zoom = action.payload.zoom;
    },

    /** Point at a location the operator searched for. Replaces any previous. */
    searchPinDropped(state, action: PayloadAction<SearchPin>) {
      state.searchPin = action.payload;
    },

    /**
     * Stop pointing. Dispatched when the operator clears the search box or
     * dismisses the pin — panning away does not clear it, because "where was
     * that address again" is the reason to keep it.
     */
    searchPinCleared(state) {
      state.searchPin = null;
    },
  },
});

export const { mapViewChanged, searchPinDropped, searchPinCleared } =
  mapViewSlice.actions;
export default mapViewSlice.reducer;
